/**
 * Daily Log API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  DailyLog,
  DailyLogListParams,
  DailyLogListResponse,
  DailyLogStats,
  WorkforceSummary,
  WorkforceSummaryParams,
  DelaysSummary,
  DelaysSummaryParams,
  CalendarData,
  CalendarParams,
  CreateDailyLogRequest,
  UpdateDailyLogRequest,
  SignatureRequest,
  RejectDailyLogRequest,
  AddPhotosRequest,
} from '../types/dailyLog';

/**
 * Daily Log API Endpoints
 */
export const dailyLogApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // DAILY LOG CRUD
    // ============================================================================

    // Get all daily logs for a project
    getDailyLogs: builder.query<
      ApiResponse<DailyLogListResponse>,
      { projectId: string; params?: DailyLogListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/daily-logs`,
        params,
      }),
      providesTags: ['DailyLog'],
    }),

    // Get single daily log
    getDailyLog: builder.query<ApiResponse<DailyLog>, { projectId: string; logId: string }>({
      query: ({ projectId, logId }) => ({
        url: `/projects/${projectId}/daily-logs/${logId}`,
      }),
      providesTags: (_result, _error, { logId }) => [{ type: 'DailyLog', id: logId }],
    }),

    // Create daily log
    createDailyLog: builder.mutation<
      ApiResponse<DailyLog>,
      { projectId: string; data: CreateDailyLogRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/daily-logs`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['DailyLog'],
    }),

    // Update daily log
    updateDailyLog: builder.mutation<
      ApiResponse<DailyLog>,
      { projectId: string; logId: string; data: UpdateDailyLogRequest }
    >({
      query: ({ projectId, logId, data }) => ({
        url: `/projects/${projectId}/daily-logs/${logId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { logId }) => [
        'DailyLog',
        { type: 'DailyLog', id: logId },
      ],
    }),

    // Delete daily log
    deleteDailyLog: builder.mutation<ApiResponse<void>, { projectId: string; logId: string }>({
      query: ({ projectId, logId }) => ({
        url: `/projects/${projectId}/daily-logs/${logId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DailyLog'],
    }),

    // ============================================================================
    // WORKFLOW OPERATIONS
    // ============================================================================

    // Submit for approval
    submitDailyLog: builder.mutation<
      ApiResponse<DailyLog>,
      { projectId: string; logId: string; data: SignatureRequest }
    >({
      query: ({ projectId, logId, data }) => ({
        url: `/projects/${projectId}/daily-logs/${logId}/submit`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { logId }) => [
        'DailyLog',
        { type: 'DailyLog', id: logId },
      ],
    }),

    // Approve daily log
    approveDailyLog: builder.mutation<
      ApiResponse<DailyLog>,
      { projectId: string; logId: string; data: SignatureRequest }
    >({
      query: ({ projectId, logId, data }) => ({
        url: `/projects/${projectId}/daily-logs/${logId}/approve`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { logId }) => [
        'DailyLog',
        { type: 'DailyLog', id: logId },
      ],
    }),

    // Reject daily log
    rejectDailyLog: builder.mutation<
      ApiResponse<DailyLog>,
      { projectId: string; logId: string; data: RejectDailyLogRequest }
    >({
      query: ({ projectId, logId, data }) => ({
        url: `/projects/${projectId}/daily-logs/${logId}/reject`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { logId }) => [
        'DailyLog',
        { type: 'DailyLog', id: logId },
      ],
    }),

    // ============================================================================
    // PHOTO OPERATIONS
    // ============================================================================

    // Add photos
    addDailyLogPhotos: builder.mutation<
      ApiResponse<DailyLog>,
      { projectId: string; logId: string; data: AddPhotosRequest }
    >({
      query: ({ projectId, logId, data }) => ({
        url: `/projects/${projectId}/daily-logs/${logId}/photos`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { logId }) => [
        'DailyLog',
        { type: 'DailyLog', id: logId },
      ],
    }),

    // Remove photo
    removeDailyLogPhoto: builder.mutation<
      ApiResponse<DailyLog>,
      { projectId: string; logId: string; photoId: string }
    >({
      query: ({ projectId, logId, photoId }) => ({
        url: `/projects/${projectId}/daily-logs/${logId}/photos/${photoId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { logId }) => [
        'DailyLog',
        { type: 'DailyLog', id: logId },
      ],
    }),

    // ============================================================================
    // STATISTICS & REPORTS
    // ============================================================================

    // Get daily log statistics
    getDailyLogStats: builder.query<ApiResponse<DailyLogStats>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/daily-logs/stats`,
      }),
      providesTags: ['DailyLog'],
    }),

    // Get workforce summary
    getWorkforceSummary: builder.query<
      ApiResponse<WorkforceSummary>,
      { projectId: string; params: WorkforceSummaryParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/daily-logs/workforce-summary`,
        params,
      }),
      providesTags: ['DailyLog'],
    }),

    // Get delays summary
    getDelaysSummary: builder.query<
      ApiResponse<DelaysSummary>,
      { projectId: string; params: DelaysSummaryParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/daily-logs/delays-summary`,
        params,
      }),
      providesTags: ['DailyLog'],
    }),

    // Get calendar data
    getDailyLogCalendar: builder.query<
      ApiResponse<CalendarData[]>,
      { projectId: string; params?: CalendarParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/daily-logs/calendar`,
        params,
      }),
      providesTags: ['DailyLog'],
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  // CRUD
  useGetDailyLogsQuery,
  useGetDailyLogQuery,
  useCreateDailyLogMutation,
  useUpdateDailyLogMutation,
  useDeleteDailyLogMutation,
  // Workflow
  useSubmitDailyLogMutation,
  useApproveDailyLogMutation,
  useRejectDailyLogMutation,
  // Photos
  useAddDailyLogPhotosMutation,
  useRemoveDailyLogPhotoMutation,
  // Statistics & Reports
  useGetDailyLogStatsQuery,
  useGetWorkforceSummaryQuery,
  useGetDelaysSummaryQuery,
  useGetDailyLogCalendarQuery,
} = dailyLogApi;

/**
 * Utility functions for PDF operations
 */
export const getDailyLogPdfUrl = (projectId: string, logId: string): string => {
  return `/api/projects/${projectId}/daily-logs/${logId}/pdf`;
};

export const getDailyLogPdfPreviewUrl = (projectId: string, logId: string): string => {
  return `/api/projects/${projectId}/daily-logs/${logId}/pdf/preview`;
};

export const downloadDailyLogPdf = async (
  projectId: string,
  logId: string,
  logNumber: string
): Promise<void> => {
  const url = getDailyLogPdfUrl(projectId, logId);
  const token = localStorage.getItem('auth_token');

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download PDF');
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `${logNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * Re-export types for convenience
 */
export type { DailyLogListParams } from '../types/dailyLog';
